/// <amd-module name="Analytics/Boards/CumulativeFlowDiagramQueries/CumulativeFlowHistoryQuery" />
import { AnalyticsCacheableQueryBase } from "Analytics/Common/AnalyticsCacheableQueryBase";
import { ODataQueryOptions } from "Analytics/Common/ODataQueryOptions";
import { DateSKParser } from "Analytics/Common/Utilities/DateSKParser";
import { latestODataVersion, QueryUtilities } from "Analytics/Common/Utilities/QueryUtilities";

/**  A sample point in the CFD history. */
export interface CFDSamplePoint {
    /** The number of work items */
    Count: number;

    /** The date at which the data point was sampled (account time zone). */
    SampleDate: string;

    /** The ID of the column represented by this sample point. */
    ColumnId: string;
}

export interface CumulativeFlowHistoryQueryParameters {
    project: string;
    boardId: string;
    /**
     * (Optional)
     * The name of a swimlane on a board.
     * A value of null or undefined means don't filter on lane.
     */
    boardLane?: string | null;
    /** A string in the format "yyyy-MM-dd" */
    startDate: string;
}

/** The format of the data returned by the OData query representing a sample point in the CFD history. */
interface AggregatedWorkItemBoardSnapshot {
    /** The number of work items */
    Count: number;

    /**
     * A surrogate key for the date at which the data point was sampled.
     * Analytics implements this as a number constructed from the sample date formatted as "yyyyMMdd".
     * So a sample on 2016-01-02 would have a DateSK of 20160102.
     * We ask for DateSK instead of Date/Date to improve performance. Asking for Date/Date requires
     * lookups in the SQL backend of Analytics whereas we can just query using DateSK instead to get the same
     * information without Analytics having to do the lookups.
     */
    DateSK: number;

    /** The ID of the column represented by this sample point. */
    ColumnId: string;
}

/**
 * Returns trend history for a CFD chart.
 * Data is ordered by column id then by date in ascending order.
 */
export class CumulativeFlowHistoryQuery extends AnalyticsCacheableQueryBase<CFDSamplePoint[]> {
    private static generateQueryOptions(args: CumulativeFlowHistoryQueryParameters): ODataQueryOptions {
        return {
            entityType: "WorkItemBoardSnapshot",
            oDataVersion: latestODataVersion,
            project: args.project,
            $apply: CumulativeFlowHistoryQuery.createApplyQueryOption(args),
            $orderby: "ColumnId,DateSK",
            followNextLink: true // To make sure we retrieve all results
        };
    }

    private static createApplyQueryOption({ boardId, boardLane, startDate }: CumulativeFlowHistoryQueryParameters): string {
        const applyClauses = [`filter(BoardId eq ${boardId} and DateSK ge ${DateSKParser.dateStringToDateSK(startDate)})`];

        if (boardLane != null) {
            applyClauses.push(`filter(LaneName eq '${QueryUtilities.escapeString(boardLane)}')`);
        }

        applyClauses.push("groupby((DateSK,ColumnId), aggregate($count as Count))");

        return applyClauses.join("/");
    }

    constructor(command: string, args: CumulativeFlowHistoryQueryParameters) {
        super(command, CumulativeFlowHistoryQuery.generateQueryOptions(args));
    }

    public getQueryName(): string {
        return "CumulativeFlowHistoryQuery";
    }

    protected interpretQueryResults({ value }: { value: AggregatedWorkItemBoardSnapshot[] }): CFDSamplePoint[] {
        return value.map(snapshot => {
            return {
                Count: snapshot.Count,
                ColumnId: snapshot.ColumnId,
                SampleDate: DateSKParser.parseDateSKAsDateString(snapshot.DateSK)
            };
        });
    }
}
